/**
 * Utility for exporting digital card data to various formats
 * This module provides functionality to export user data as a VCF file (vCard)
 * that can be imported into contact applications.
 */
const CardExporter = {
    /**
     * Export user data as a VCF file (vCard format)
     * @param {Object} userData - User data to export
     * @param {string} userData.name - User's full name
     * @param {string} userData.email - User's primary email
     * @param {string} userData.forwardingEmail - User's forwarding email (optional)
     * @param {string} userData.title - User's title/profession (optional)
     * @param {string} userData.bio - User's bio (optional)
     * @param {string} userData.username - User's username (optional)
     * @param {string} userData.userId - User's ID in the system (optional)
     * @param {Object} userData.curationSignature - User's preferences (optional)
     */
    exportToVCF: (userData) => {
      try {
        // Start building VCF content
        let vcfContent = [
          'BEGIN:VCARD',
          'VERSION:3.0',
          `FN:${userData.name || 'Unknown Name'}`
        ];
        
        // Add organization and title if available
        if (userData.title) {
          vcfContent.push(`TITLE:${userData.title}`);
        }
        
        // Add emails
        if (userData.email) {
          vcfContent.push(`EMAIL;type=INTERNET;type=HOME:${userData.email}`);
        }
        
        if (userData.forwardingEmail) {
          vcfContent.push(`EMAIL;type=INTERNET;type=WORK:${userData.forwardingEmail}`);
        }
        
        // Add notes section with bio and preferences if available
        const notes = [];
        
        if (userData.bio) {
          notes.push(userData.bio);
        }
        
        // Add preferences as notes
        if (userData.curationSignature) {
          const { preferredTags, materialBias, colorPalette, influences } = userData.curationSignature;
          
          if (preferredTags && preferredTags.length > 0) {
            notes.push(`Design Styles: ${preferredTags.join(', ')}`);
          }
          
          if (materialBias && materialBias.length > 0) {
            notes.push(`Materials: ${materialBias.join(', ')}`);
          }
          
          if (colorPalette && colorPalette.length > 0) {
            notes.push(`Colors: ${colorPalette.join(', ')}`);
          }
          
          if (influences && influences.length > 0) {
            notes.push(`Influences: ${influences.join(', ')}`);
          }
        }
        
        if (notes.length > 0) {
          // Escape special characters for VCF format
          const escapedNotes = notes.join('\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
          vcfContent.push(`NOTE:${escapedNotes}`);
        }
        
        // Add URL for Artifact profile
        if (userData.username) {
          vcfContent.push(`URL:https://artifact.app/profile/${userData.username}`);
        }
        
        // Add unique identifier
        if (userData.userId) {
          vcfContent.push(`UID:${userData.userId}`);
        }
        
        // Add Artifact specific data as X- properties
        vcfContent.push('X-ARTIFACT-PROFILE:true');
        
        if (userData.visibilityMode) {
          vcfContent.push(`X-ARTIFACT-VISIBILITY:${userData.visibilityMode}`);
        }
        
        // End VCF
        vcfContent.push('END:VCARD');
        
        // Join with CRLF as required by VCF format
        const vcfString = vcfContent.join('\r\n');
        
        // Create and download the file
        const blob = new Blob([vcfString], { type: 'text/vcard' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${userData.name.replace(/\s+/g, '_')}_artifact.vcf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        return true;
      } catch (error) {
        console.error('Error exporting to VCF:', error);
        return false;
      }
    }
  };
  
  export default CardExporter;